/**
 * Console pattern: responder/Hotlines — Hotline 管理.
 *
 * Spec §5.4. Top actions, platform-mode banner, list of hotlines with
 * adapter/状态/task_types chips, enable Switch, draft drawer (5 tab).
 *
 * 静态原型：草稿 drawer 用 inline panel（非 portal）展开在选中行下方，5 tab
 * 切换 demo 内容契约里要求的 5 段视图。
 */

import { useState } from "react";
import {
  Cpu,
  Globe2,
  Plus,
  Sparkles,
  Trash2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";

import {
  ConsolePageHeader,
  ConsoleCard,
  ConsoleButton,
} from "../shells/console-shell";
import { cn } from "@/app/components/ui/utils";

type AdapterType = "process" | "http";
type ReviewState = "official" | "draft" | "needs_revision" | "rejected";

type Hotline = {
  hotlineId: string;
  displayName: string;
  adapter: AdapterType;
  endpoint: string;
  taskTypes: string[];
  capabilities: string[];
  tags: string[];
  softTimeoutMs: number;
  hardTimeoutMs: number;
  enabled: boolean;
  loadedLocally: boolean;
  draftReady: boolean;
  reviewState: ReviewState;
  description: string;
};

const HOTLINES: Hotline[] = [
  {
    hotlineId: "local.workspace-summary.v1",
    displayName: "工作区文档摘要",
    adapter: "process",
    endpoint: "node ./bin/summarize.mjs",
    taskTypes: ["summarize-document"],
    capabilities: ["read-fs", "no-network"],
    tags: ["local", "private"],
    softTimeoutMs: 8000,
    hardTimeoutMs: 30000,
    enabled: true,
    loadedLocally: true,
    draftReady: true,
    reviewState: "official",
    description: "将给定路径或工作区根下的文档汇总成 200 字以内的中文摘要。",
  },
  {
    hotlineId: "ops.notify@v1",
    displayName: "Slack 通知 · #ops 频道",
    adapter: "http",
    endpoint: "https://hooks.slack.com/services/xxx",
    taskTypes: ["send-notification", "alert-team"],
    capabilities: ["network:slack.com"],
    tags: ["team", "messaging"],
    softTimeoutMs: 3000,
    hardTimeoutMs: 8000,
    enabled: true,
    loadedLocally: true,
    draftReady: true,
    reviewState: "draft",
    description: "向团队 #ops 频道发送一条 Markdown 通知。",
  },
  {
    hotlineId: "calendar.create-event",
    displayName: "Google 日历 · 创建事件",
    adapter: "http",
    endpoint: "https://www.googleapis.com/calendar/v3/…",
    taskTypes: ["schedule-meeting"],
    capabilities: ["network:googleapis.com", "scope:calendar.events.write"],
    tags: ["productivity"],
    softTimeoutMs: 3000,
    hardTimeoutMs: 8000,
    enabled: false,
    loadedLocally: true,
    draftReady: true,
    reviewState: "needs_revision",
    description: "在主日历创建事件；自动避开会议冲突。",
  },
];

const ADAPTER_LABEL: Record<AdapterType, { label: string; Icon: typeof Cpu }> = {
  process: { label: "process", Icon: Cpu },
  http: { label: "http", Icon: Globe2 },
};

const REVIEW_BADGE: Record<ReviewState, { label: string; cls: string; hint: string }> = {
  official: { label: "official", cls: "c-status-success", hint: "已通过平台审核，全网可见。" },
  draft: { label: "draft", cls: "c-status-info", hint: "本地草稿，可本地调用，未提交平台。" },
  needs_revision: { label: "需修改", cls: "c-status-warn", hint: "平台已审，存在问题，参见 review 备注。" },
  rejected: { label: "已驳回", cls: "c-status-error", hint: "平台审核未通过；不会出现在公开 Catalog。" },
};

export function ConsolePageHotlines() {
  const [list, setList] = useState(HOTLINES);
  const [openDraft, setOpenDraft] = useState<string | null>(null);

  const toggleEnable = (id: string) =>
    setList((prev) => prev.map((h) => (h.hotlineId === id ? { ...h, enabled: !h.enabled } : h)));

  return (
    <>
      <ConsolePageHeader
        kicker="RESPONDER · Hotline 管理"
        title="把你写的能力发布成 Hotline"
        description="本机已加载的 Hotline 列表。开启后 Caller 可调用；草稿可本地试拨。点「草稿」查看 5 段视图。"
        actions={
          <>
            <ConsoleButton variant="ghost">
              <Sparkles className="h-3.5 w-3.5" />
              添加示例
            </ConsoleButton>
            <ConsoleButton variant="primary">
              <Plus className="h-3.5 w-3.5" />
              添加
            </ConsoleButton>
          </>
        }
      />

      {/* Platform-mode banner */}
      <div
        className="mb-5 p-3 border-l-[3px] border-l-[var(--brand-purple)] bg-[color-mix(in_oklab,var(--brand-purple)_8%,transparent)] flex items-start gap-3"
        style={{ borderRadius: "var(--c-radius-sm)" }}
      >
        <AlertTriangle className="h-4 w-4 mt-0.5 text-[var(--brand-purple)] shrink-0" strokeWidth={2.5} />
        <div className="flex-1">
          <p className="text-[12.5px] font-semibold text-[var(--ink)]">
            平台模式未开启 · Hotline 仅在本机可用
          </p>
          <p className="text-[11.5px] text-[var(--brand-muted)] mt-0.5">
            打开平台模式后，official 状态的 Hotline 会进入官方 Marketplace 被外部 Caller 发现。Draft 永远只在本地。
          </p>
        </div>
        <Link
          to="/console/dashboard?from=hotlines-banner"
          className="text-[11.5px] font-semibold text-[var(--ink)] underline underline-offset-2 hover:text-[var(--brand-purple)] shrink-0"
        >
          打开平台模式 →
        </Link>
      </div>

      {/* List */}
      <ConsoleCard padded={false} className="overflow-hidden">
        <div className="px-5 py-2.5 border-b flex items-center justify-between bg-[var(--brand-secondary)]/30" style={{ borderColor: "var(--border)" }}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--brand-muted)]">
            本机 Hotline · {list.length}
          </p>
          <span className="text-[10.5px] text-[var(--brand-muted)] font-mono">
            GET /responder/hotlines
          </span>
        </div>

        <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
          {list.map((h) => {
            const Adapter = ADAPTER_LABEL[h.adapter].Icon;
            const review = REVIEW_BADGE[h.reviewState];
            const isOpen = openDraft === h.hotlineId;
            return (
              <li key={h.hotlineId}>
                <div className="px-5 py-3.5 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Top line */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[14px] font-semibold text-[var(--ink)] truncate">
                        {h.displayName}
                      </p>
                      <span className={cn("c-status text-[10px]", review.cls)} title={review.hint}>
                        {review.label}
                      </span>
                      {h.draftReady && (
                        <span className="c-status text-[10px] c-status-info">草稿就绪</span>
                      )}
                      {h.loadedLocally && (
                        <span className="c-status text-[10px] c-status-success">本地已加载</span>
                      )}
                    </div>

                    {/* mono id */}
                    <p className="text-[11.5px] font-mono text-[var(--brand-muted)] mt-1 truncate">
                      {h.hotlineId}
                    </p>

                    {/* chips line */}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <Chip>
                        <Adapter className="h-2.5 w-2.5" strokeWidth={2.5} />
                        {ADAPTER_LABEL[h.adapter].label}
                      </Chip>
                      {h.taskTypes.map((t) => (
                        <Chip key={t} tone="task">{t}</Chip>
                      ))}
                      <span className="text-[10.5px] text-[var(--brand-muted)] ml-1">
                        timeout {h.softTimeoutMs / 1000}s / {h.hardTimeoutMs / 1000}s
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={() => setOpenDraft(isOpen ? null : h.hotlineId)}
                      className="text-[12px] font-semibold text-[var(--ink)] inline-flex items-center gap-1 hover:text-[var(--brand-orange)]"
                    >
                      {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      草稿
                    </button>
                    <Switch enabled={h.enabled} onChange={() => toggleEnable(h.hotlineId)} />
                    <button
                      className="text-[var(--brand-muted)] hover:text-[var(--c-status-error-fg)]"
                      title="删除（仅本地，不影响平台）"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                    </button>
                  </div>
                </div>

                {isOpen && <DraftPanel hotline={h} />}
              </li>
            );
          })}
        </ul>
      </ConsoleCard>
    </>
  );
}

function Chip({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "task";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 text-[10.5px] font-semibold border",
        tone === "task"
          ? "bg-[var(--brand-secondary)]/60 border-[var(--brand-secondary)] text-[var(--ink)]"
          : "bg-white border-[var(--border)] text-[var(--brand-muted)]",
      )}
      style={{ borderRadius: "var(--c-radius-sm)" }}
    >
      {children}
    </span>
  );
}

function Switch({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className={cn(
        "inline-flex items-center gap-2 px-2 py-1 border text-[11px] font-semibold",
        enabled
          ? "bg-[var(--brand-green)] border-[var(--brand-ink)] text-[var(--ink)]"
          : "bg-white border-[var(--border)] text-[var(--brand-muted)]",
      )}
      style={{ borderRadius: "var(--c-radius-sm)" }}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          enabled ? "bg-[var(--ink)]" : "bg-[var(--brand-muted)]",
        )}
      />
      {enabled ? "ENABLED" : "DISABLED"}
    </button>
  );
}

const DRAFT_TABS = ["概览", "契约", "示例", "使用建议", "原始"] as const;
type DraftTab = (typeof DRAFT_TABS)[number];

function DraftPanel({ hotline }: { hotline: Hotline }) {
  const [tab, setTab] = useState<DraftTab>("概览");
  return (
    <div className="border-t bg-[var(--brand-secondary)]/30 px-5 py-4" style={{ borderColor: "var(--border)" }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--brand-muted)]">
          DraftDocument · {hotline.hotlineId}
        </p>
        <a
          className="text-[11px] font-medium text-[var(--brand-muted)] hover:text-[var(--ink)] inline-flex items-center gap-1"
          href="#"
        >
          全屏查看 <ExternalLink className="h-2.5 w-2.5" />
        </a>
      </div>

      <div className="flex gap-1 mb-3 border-b" style={{ borderColor: "var(--border)" }}>
        {DRAFT_TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              "px-3 py-1.5 -mb-px text-[12px] font-semibold border-b-2",
              tab === t
                ? "border-[var(--brand-ink)] text-[var(--ink)]"
                : "border-transparent text-[var(--brand-muted)] hover:text-[var(--ink)]",
            )}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="bg-white border p-4" style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}>
        {tab === "概览" && (
          <DraftOverview hotline={hotline} />
        )}
        {tab === "契约" && (
          <DraftContract />
        )}
        {tab === "示例" && (
          <DraftExample />
        )}
        {tab === "使用建议" && (
          <p className="text-[12.5px] text-[var(--ink)]/80 leading-relaxed">
            建议在 Caller 的 Approval 列表里把本 hotline 加入白名单（按 hotline_id 而非 Responder），
            这样 Agent 触发时不会逐次打断你；高风险动作仍由 capabilities 里的 scope 限制兜底。
          </p>
        )}
        {tab === "原始" && (
          <pre className="text-[11.5px] font-mono leading-relaxed m-0 overflow-x-auto">
{JSON.stringify(
  {
    hotline_id: hotline.hotlineId,
    display_name: hotline.displayName,
    adapter_type: hotline.adapter,
    endpoint: hotline.endpoint,
    task_types: hotline.taskTypes,
    capabilities: hotline.capabilities,
    tags: hotline.tags,
    soft_timeout_ms: hotline.softTimeoutMs,
    hard_timeout_ms: hotline.hardTimeoutMs,
    review_state: hotline.reviewState,
  },
  null,
  2,
)}
          </pre>
        )}
      </div>
    </div>
  );
}

function DraftOverview({ hotline }: { hotline: Hotline }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[12px]">
      <Field label="display_name" value={hotline.displayName} />
      <Field label="endpoint" value={hotline.endpoint} mono />
      <Field
        label="task_types"
        value={hotline.taskTypes.join(", ") || "—"}
        mono
      />
      <Field label="capabilities" value={hotline.capabilities.join(" · ") || "—"} mono />
      <Field label="tags" value={hotline.tags.join(" · ") || "—"} />
      <Field
        label="timeouts"
        value={`soft ${hotline.softTimeoutMs}ms · hard ${hotline.hardTimeoutMs}ms`}
      />
      <div className="col-span-2">
        <p className="text-[10.5px] uppercase tracking-wider text-[var(--brand-muted)] mb-0.5">description</p>
        <p className="text-[12.5px] leading-relaxed">{hotline.description}</p>
      </div>
    </dl>
  );
}

function DraftContract() {
  return (
    <pre className="text-[11.5px] font-mono leading-relaxed m-0 overflow-x-auto">
{`input_schema: {
  doc_path: { type: "string", required: true, label: "文档路径或工作区根" }
}
output_schema: {
  summary: { type: "string", description: "200 字以内中文摘要" },
  word_count: { type: "integer" }
}`}
    </pre>
  );
}

function DraftExample() {
  return (
    <div className="text-[12px] space-y-3">
      <div>
        <p className="text-[10.5px] uppercase tracking-wider text-[var(--brand-muted)] mb-1">示例输入</p>
        <pre className="font-mono text-[11.5px] m-0 bg-[var(--brand-secondary)]/40 p-2" style={{ borderRadius: "var(--c-radius-sm)" }}>
{`{ "doc_path": "~/notes/q1-plan.md" }`}
        </pre>
      </div>
      <div>
        <p className="text-[10.5px] uppercase tracking-wider text-[var(--brand-muted)] mb-1">示例输出</p>
        <pre className="font-mono text-[11.5px] m-0 bg-[var(--brand-secondary)]/40 p-2" style={{ borderRadius: "var(--c-radius-sm)" }}>
{`{
  "summary": "Q1 计划聚焦 ops-console 重写与协议层 v1 冻结，… ",
  "word_count": 187
}`}
        </pre>
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10.5px] uppercase tracking-wider text-[var(--brand-muted)]">{label}</p>
      <p className={cn("text-[12.5px] text-[var(--ink)]", mono && "font-mono")}>{value}</p>
    </div>
  );
}
